import {useCallback, useEffect, useRef, useState} from 'react';
import {BoundsObject} from '@/map/@types';
import {MAP_QUERY_DEBOUNCE_MS} from '@/map/constants';

export const useCreateMap = () => {
  const navermaps = naver?.maps;
  const [map, setMap] = useState<naver.maps.Map>();

  const createMap = (
    containerId: string,
    mapOptions?: naver.maps.MapOptions
  ) => {
    if (!navermaps.Map || !containerId) {
      return;
    }

    const node = document.getElementById(containerId);

    if (node) {
      const instance = new navermaps.Map(node, mapOptions);
      setMap(instance);
      return instance;
    } else {
      throw new Error('지도를 표시할 DOM을 찾을 수 없습니다.');
    }
  };

  return {map, createMap};
};

export const useMapBounds = (map: naver.maps.Map) => {
  const evt = useRef<naver.maps.MapEventListener>();
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();
  const [bounds, setBounds] = useState<BoundsObject>();

  const getBoundsObject = useCallback(() => {
    const currentBounds = map.getBounds();

    if (!currentBounds) {
      return undefined;
    }

    return {
      lat1: currentBounds.minY(),
      lat2: currentBounds.maxY(),
      lng1: currentBounds.minX(),
      lng2: currentBounds.maxX(),
    };
  }, [map]);

  useEffect(() => {
    setBounds(getBoundsObject());

    evt.current = map.addListener('idle', () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        setBounds(getBoundsObject());
      }, MAP_QUERY_DEBOUNCE_MS);
    });

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = undefined;
      }

      if (evt.current) {
        map.removeListener(evt.current);
        evt.current = undefined;
      }
    };
  }, [getBoundsObject, map]);

  return bounds;
};
